import type { ReactElement } from 'react';
import { FieldType, type FieldDefinition, type FieldValue } from '@gr/core/content/entities/fields';
import { useT } from '../../../../app/di';
import { stars } from '../../../../shared/format';
import { Combobox, type LoadOptions } from '../../../../ui-kit/Combobox/Combobox';
import { Checkbox, FormField, SelectField, TextArea, TextField } from '../../../../ui-kit/Field/Field';
import type { ReferenceLookup } from '../model/reference-search';
import styles from './FieldInput.module.scss';

export interface FieldInputProps {
  field: FieldDefinition;
  value: FieldValue | undefined;
  error?: string | null;
  disabled?: boolean;
  /** Поиск целей для поля-ссылки; его даёт подключённый компонент (useReferenceSearch). */
  references?: ReferenceLookup;
  onChange(value: FieldValue): void;
}

const RATINGS = [0.5, 1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5];

/** Без поиска (витрина без данных) поле-ссылка честно показывает «не удалось загрузить». */
const NO_REFERENCES: ReferenceLookup = { search: async () => null, titles: {} };

/** Поле формы по типу пользовательского поля (ENT-UC-5, POST-UC-2). */
export function FieldInput({ field, value, error, disabled, references, onChange }: FieldInputProps): ReactElement {
  const { t } = useT();
  const common = { label: field.label, hint: field.hint, error, required: field.required, disabled };
  const text = typeof value === 'string' || typeof value === 'number' ? String(value) : '';

  switch (field.type) {
    case FieldType.Text:
      return <TextField {...common} value={text} onChange={(e) => onChange(e.target.value)} />;
    case FieldType.LongText:
      return <TextArea {...common} rows={4} value={text} onChange={(e) => onChange(e.target.value)} />;
    case FieldType.Number:
      return <TextField {...common} type="number" step="any" inputMode="decimal" value={text} onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)} />;
    case FieldType.Date:
      return <TextField {...common} type="date" value={text} onChange={(e) => onChange(e.target.value || null)} />;
    case FieldType.Url:
      return <TextField {...common} type="url" inputMode="url" placeholder="https://" value={text} onChange={(e) => onChange(e.target.value)} />;
    case FieldType.Rating:
      return (
        <SelectField
          {...common}
          value={text}
          placeholder={t('entities.reference.choose')}
          options={RATINGS.map((r) => ({ value: String(r), label: `${r} ${stars(r)}` }))}
          onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
        />
      );
    case FieldType.Boolean:
      return (
        <FormField label={field.label} hint={field.hint} error={error}>
          {() => <Checkbox label={field.label} checked={value === true} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />}
        </FormField>
      );
    case FieldType.Select:
      return field.multiple ? (
        <FormField label={field.label} hint={field.hint} error={error} required={field.required}>
          {() => (
            <CheckboxList
              items={(field.options ?? []).map((option) => ({ id: option, title: option }))}
              selected={Array.isArray(value) ? value : []}
              disabled={disabled}
              onChange={onChange}
            />
          )}
        </FormField>
      ) : (
        <SelectField
          {...common}
          value={text}
          placeholder={t('entities.reference.choose')}
          options={(field.options ?? []).map((option) => ({ value: option, label: option }))}
          onChange={(e) => onChange(e.target.value || null)}
        />
      );
    case FieldType.Reference:
      return <ReferencePicker field={field} value={value} error={error} disabled={disabled} references={references ?? NO_REFERENCES} onChange={onChange} />;
  }
}

/** ENT-UC-8: выбор записей заданного типа или постов — автокомплит с подгрузкой с сервера. */
function ReferencePicker({ field, value, error, disabled, references, onChange }: FieldInputProps & { references: ReferenceLookup }): ReactElement {
  const { t } = useT();
  const load: LoadOptions = async (query, offset, signal) => {
    const page = await references.search(field.target, query, offset, signal);
    return page && { items: page.items.map((c) => ({ value: c.id, label: c.title })), hasMore: page.hasMore };
  };
  const common = {
    label: field.label,
    hint: field.hint,
    error,
    required: field.required,
    disabled,
    load,
    selectedLabels: references.titles,
    placeholder: t('entities.reference.search'),
  };
  return field.multiple ? (
    <Combobox {...common} multiple value={Array.isArray(value) ? value : []} onChange={onChange} />
  ) : (
    <Combobox {...common} value={typeof value === 'string' ? value : null} onChange={onChange} />
  );
}

/** Несколько значений флажками: варианты выбора или ссылки. */
function CheckboxList({ items, selected, disabled, onChange }: { items: readonly { id: string; title: string }[]; selected: readonly string[]; disabled?: boolean; onChange(value: string[]): void }): ReactElement {
  return (
    <div className={styles.options}>
      {items.map((item) => (
        <Checkbox
          key={item.id}
          label={item.title}
          checked={selected.includes(item.id)}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked ? [...selected, item.id] : selected.filter((id) => id !== item.id))}
        />
      ))}
    </div>
  );
}
